import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Mentions, Spin, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { employeesApi } from '../utils/api';
import { actionApi } from '../utils/actionApi';
import { resolveAvatarSrc } from '../utils/avatarUtils';
import './MentionTextArea.css';

/**
 * کامپوننت TextArea با قابلیت منشن کاربران
 */
const MentionTextArea = forwardRef(({
  value = '',
  onChange,
  placeholder = 'متن خود را وارد کنید...',
  rows = 4,
  maxLength = 5000,
  disabled = false,
  onMentionSelect,
  onSyncStatusChange,
  className = '',
  style = {},
}, ref) => {
  // State برای کاربران
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [options, setOptions] = useState([]);
  
  // State برای ردیابی sync در حال انجام
  const [syncingUsers, setSyncingUsers] = useState(new Set());
  
  // Ref برای ذخیره mapping منشن‌ها (این ref همیشه آخرین mappings را دارد)
  const mappingsRef = useRef([]);
  // Ref برای نگه‌داری آخرین value
  const lastValueRef = useRef(value);
  
  // Expose کردن متدها به parent
  useImperativeHandle(ref, () => ({
    // دریافت آخرین mappings
    getMappings: () => [...mappingsRef.current],
    // دریافت value فعلی
    getValue: () => lastValueRef.current,
    // پاک کردن mappings
    clearMappings: () => {
      mappingsRef.current = [];
    },
    // بررسی اینکه آیا sync در حال انجام است
    isSyncing: () => syncingUsers.size > 0,
  }));
  
  // به‌روزرسانی lastValueRef وقتی value تغییر می‌کند
  useEffect(() => {
    lastValueRef.current = value;
  }, [value]);

  // دریافت لیست کاربران ERP
  const fetchEmployees = useCallback(async (search = '') => {
    setEmployeesLoading(true);
    try {
      const filters = {};
      if (search) {
        filters.name = search;
      }
      const res = await employeesApi.getAll(filters);
      const list = (res.data || []).map((item) => ({
        id: item.id,
        name: item.name || '--',
        email: item.email || '--',
        employee_id: item.employee_detail?.employee_id || item.employeeDetail?.employee_id || null,
        image_url: item.image_url || null,
      }));
      setEmployees(list);
      
      // ساخت options
      const opts = list.map((user) => ({
        value: user.name,
        label: (
          <div className="mention-option-item">
            <Avatar
              src={resolveAvatarSrc(user.image_url)}
              icon={<UserOutlined />}
              size={32}
              style={{ backgroundColor: '#1890ff' }}
            />
            <div className="mention-option-info">
              <span className="mention-option-name">{user.name}</span>
              <span className="mention-option-email">{user.email}</span>
            </div>
          </div>
        ),
      }));
      setOptions(opts);
    } catch (e) {
      console.error('خطا در دریافت لیست کاربران:', e);
      setEmployees([]);
      setOptions([]);
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  // بارگذاری اولیه کاربران
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Handle search in mentions - این تابع وقتی @ تایپ می‌شود صدا زده می‌شود
  const handleSearch = (search) => {
    fetchEmployees(search);
  };

  // Handle select user
  const handleSelect = async (option) => {
    const user = employees.find((emp) => emp.name === option.value);
    if (!user) return;

    console.log('User selected:', user);

    // اضافه کردن user ID به لیست sync در حال انجام
    setSyncingUsers((prev) => {
      const newSet = new Set([...prev, user.id]);
      // اطلاع دادن به parent که sync شروع شده
      if (onSyncStatusChange) {
        onSyncStatusChange(true);
      }
      return newSet;
    });

    // فراخوانی API sync برای دریافت username
    let username = null;
    try {
      const syncResponse = await actionApi.syncUserFromErp(user.id);
      console.log('Full sync response:', JSON.stringify(syncResponse, null, 2));
      
      // طبق ساختار response که کاربر داد: syncResponse.data.username
      username = syncResponse?.data?.username || null;
      
      console.log('Extracted username:', username);
      
      if (!username) {
        console.warn('Username not found in sync response. Response structure:', {
          hasData: !!syncResponse?.data,
          dataKeys: syncResponse?.data ? Object.keys(syncResponse.data) : [],
          fullResponse: syncResponse
        });
      } else {
        console.log('✅ User synced successfully:', user.id, 'username:', username);
      }
    } catch (error) {
      console.error('Error syncing user:', error);
    } finally {
      // حذف user ID از لیست sync در حال انجام (بعد از اتمام sync)
      setSyncingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        const isStillSyncing = newSet.size > 0;
        // اطلاع دادن به parent که sync تمام شده (یا هنوز در حال انجام است)
        if (onSyncStatusChange) {
          onSyncStatusChange(isStillSyncing);
        }
        return newSet;
      });
    }

    // ذخیره mapping با username
    const newMapping = {
      name: user.name, // برای نمایش در UI (مثلاً "محمد جوادی اصل")
      email: user.email,
      erpUserId: user.id,
      username: username, // username از response API sync (مثلاً "محمد_جوادی_اصل_11")
    };

    // جلوگیری از duplicate بر اساس email
    const existingIndex = mappingsRef.current.findIndex((m) => m.email === user.email);
    if (existingIndex >= 0) {
      mappingsRef.current[existingIndex] = newMapping;
      console.log('Updated existing mapping:', newMapping);
    } else {
      mappingsRef.current = [...mappingsRef.current, newMapping];
      console.log('Added new mapping:', newMapping);
    }

    console.log('All current mappings:', JSON.stringify(mappingsRef.current, null, 2));

    // به‌روزرسانی onChange با mapping های جدید بعد از یک تاخیر کوتاه
    setTimeout(() => {
      if (onChange) {
        const currentValue = lastValueRef.current || value;
        console.log('Calling onChange after sync:', { currentValue, mappings: mappingsRef.current });
        onChange(currentValue, [...mappingsRef.current]);
      }
    }, 150);

    // Callback
    if (onMentionSelect) {
      onMentionSelect(user);
    }
  };

  // Handle change
  const handleChange = (newValue) => {
    lastValueRef.current = newValue;
    
    if (onChange) {
      onChange(newValue, [...mappingsRef.current]);
    }
  };

  return (
    <div className={`mention-textarea-wrapper ${className}`}>
      <Mentions
        value={value}
        onChange={handleChange}
        onSearch={handleSearch}
        onSelect={handleSelect}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        loading={employeesLoading}
        options={options}
        style={{
          width: '100%',
          fontFamily: 'CustomFont',
          ...style,
        }}
        notFoundContent={
          employeesLoading ? (
            <div className="mention-loading">
              <Spin size="small" />
              <span>در حال بارگذاری...</span>
            </div>
          ) : (
            <div className="mention-not-found">
              کاربری یافت نشد
            </div>
          )
        }
        autoSize={{ minRows: rows, maxRows: 8 }}
      />

      {/* نمایش راهنما و شمارشگر */}
      <div className="mention-footer">
        <span className="mention-hint">
          💡 برای منشن کردن @ را تایپ کنید
        </span>
        {maxLength && (
          <span className="mention-counter">
            {value.length} / {maxLength}
          </span>
        )}
      </div>
    </div>
  );
});

MentionTextArea.displayName = 'MentionTextArea';

export default MentionTextArea;

/**
 * تابع کمکی برای تبدیل محتوا قبل از ارسال به بک‌اند
 * @name را به @username تبدیل می‌کند (از response API sync)
 */
export const convertMentionsToEmails = (content, mappings) => {
  if (!content) {
    console.log('convertMentionsToEmails: No content');
    return content;
  }
  
  if (!mappings || mappings.length === 0) {
    console.log('convertMentionsToEmails: No mappings provided');
    return content;
  }

  console.log('convertMentionsToEmails: Starting conversion');
  console.log('Input content:', content);
  console.log('Mappings:', JSON.stringify(mappings, null, 2));
  
  let result = content;
  
  for (const mapping of mappings) {
    if (!mapping.name) {
      console.log('Skipping mapping without name:', mapping);
      continue;
    }
    
    // اول بررسی کنیم که این نام در محتوا وجود دارد یا نه
    const checkPattern = `@${mapping.name}`;
    if (!result.includes(checkPattern)) {
      console.log(`Name "${mapping.name}" not found in content, skipping`);
      continue;
    }
    
    if (!mapping.username) {
      // اگر username موجود نباشد، از email استفاده می‌کنیم (fallback)
      console.warn(`⚠️ Username not found for "${mapping.name}", using email: ${mapping.email}`);
      const namePattern = new RegExp(`@${escapeRegExp(mapping.name)}(?=[\\s]|$|@|\\.|,|!|\\?|;|:|\\)|\\(|\\[|\\]|\\{|\\})`, 'g');
      const beforeReplace = result;
      result = result.replace(namePattern, `@${mapping.email}`);
      if (beforeReplace !== result) {
        console.log(`Replaced @${mapping.name} with @${mapping.email}`);
      }
    } else {
      // تبدیل @name به @username
      console.log(`Converting @${mapping.name} to @${mapping.username}`);
      const namePattern = new RegExp(`@${escapeRegExp(mapping.name)}(?=[\\s]|$|@|\\.|,|!|\\?|;|:|\\)|\\(|\\[|\\]|\\{|\\})`, 'g');
      const beforeReplace = result;
      result = result.replace(namePattern, `@${mapping.username}`);
      if (beforeReplace !== result) {
        console.log(`✅ Successfully replaced @${mapping.name} with @${mapping.username}`);
      } else {
        console.warn(`⚠️ Pattern not matched for @${mapping.name}`);
      }
    }
  }
  
  console.log('Final output:', result);
  return result;
};

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const convertEmailsToNames = (content, users) => {
  let result = content;
  for (const user of users) {
    if (user.email && user.name) {
      const emailPattern = new RegExp(`@${escapeRegExp(user.email)}\\b`, 'g');
      result = result.replace(emailPattern, `@${user.name}`);
    }
  }
  return result;
};

/**
 * تابع برای render کردن متن با منشن‌های استایل شده
 * این تابع متن را parse می‌کند و @username را با استایل خاص نمایش می‌دهد
 * همچنین سطرهای جدید (\n) را رعایت می‌کند
 * 
 * @param {string} content - محتوای کامنت
 * @returns {ReactNode} - JSX با منشن‌های استایل شده
 */
export const renderContentWithMentions = (content) => {
  if (!content) return null;

  // ابتدا متن را بر اساس خط جدید تقسیم می‌کنیم
  const lines = content.split('\n');
  const result = [];
  let keyCounter = 0;

  lines.forEach((line, lineIndex) => {
    // پردازش هر خط برای یافتن منشن‌ها
    const lineParts = processLineWithMentions(line, keyCounter);
    keyCounter += lineParts.mentionCount;
    result.push(...lineParts.parts);

    // اضافه کردن <br /> بعد از هر خط به جز آخری
    if (lineIndex < lines.length - 1) {
      result.push(<br key={`br-${lineIndex}`} />);
    }
  });

  return <span>{result}</span>;
};

/**
 * تابع کمکی برای پردازش یک خط و یافتن منشن‌ها
 */
const processLineWithMentions = (line, startKeyCounter) => {
  // Pattern برای پیدا کردن منشن‌ها: @نام یا @username
  // پشتیبانی از حروف فارسی، انگلیسی، اعداد، زیرخط و فاصله
  const mentionPattern = /@([\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-Z0-9_]+)(?=[\s]|$|@|\.|,|!|\?|;|:|\)|\(|\[|\]|{|})/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyCounter = startKeyCounter;
  let mentionCount = 0;

  while ((match = mentionPattern.exec(line)) !== null) {
    // متن قبل از منشن
    if (match.index > lastIndex) {
      const beforeText = line.substring(lastIndex, match.index);
      if (beforeText) {
        parts.push(beforeText);
      }
    }

    // منشن با استایل
    const mentionText = match[0]; // شامل @ هم می‌شود
    parts.push(
      <span key={`mention-${keyCounter++}`} className="mention-tag">
        {mentionText}
      </span>
    );
    mentionCount++;

    lastIndex = mentionPattern.lastIndex;
  }

  // متن باقیمانده
  if (lastIndex < line.length) {
    const remainingText = line.substring(lastIndex);
    if (remainingText) {
      parts.push(remainingText);
    }
  }

  // اگر خط خالی است، یک string خالی برگردان
  if (parts.length === 0 && line === '') {
    // خط خالی - نیازی به اضافه کردن چیزی نیست چون <br /> اضافه می‌شود
  }

  return { parts, mentionCount };
};
